"use client";
import {
	ChangeEventHandler,
	Dispatch,
	FC,
	FormEventHandler,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";
import TextField from "elements/Input/TextField";
import { AutocompleteItem, IAddress } from "utils/types";
import StateSelect from "elements/Input/StateSelect";
import { useAppealContext } from "context/AppealContext";
import { useSearchParams, useRouter } from "next/navigation";
import withAppealFlow from "./withAppealFlow";
import cleanAddress from "utils/cleanAddress";
import extractLine2 from "utils/extractLine2";
import Grid from "elements/Grid";
import states from "public/states.json";
import { QuickLink } from "components";
import { Label } from "elements/Input";
import toTitleCase from "utils/toTitleCase";
import { useEventTracker } from "utils/useEventTracker";
import SimpleSpinner from "public/landing/simple-spinner.gif";
import NextImage from "next/image";
import { useAutocomplete } from "utils/useAutocomplete";
import { useAuthContext } from "context/AuthContext";
import Search from "elements/Input/Search";
import constants from "utils/constants";
import Head from "next/head";
import ButtonV2 from "elements/ButtonV2";

const cleanUrlVals = (key: string, value: string) => {
	if (key === "zipcode" && value && value.length > 5) {
		return "";
	} else if (key === "state" && value) {
		value = value.toUpperCase();
		let stateNameLong;
		if (value.length === 2) {
			stateNameLong = states[value as keyof typeof states];
			if (stateNameLong === undefined) {
				return "";
			}
		} else if (value.length > 2) {
			value =
				Object.keys(states).find(
					key => states[key as keyof typeof states].toUpperCase() === value
				) || "";
		}
	}
	return value;
};

const AutocompleteAddressEntry: FC<{
	setAddressInputType: Dispatch<SetStateAction<"autocomplete" | "manual">>;
}> = ({ setAddressInputType }) => {
	const { setAddress, startAppealFlow, setIsExemptionsEndToEndFlow } =
		useAppealContext();

	const [query, setQuery] = useState("");
	const [showResults, setShowResults] = useState(false);
	const [activeIndex, setActiveIndex] = useState<number>();

	const { autoCompleteLoading, autoCompleteResults } = useAutocomplete(query);

	const trackEvent = useEventTracker();

	const router = useRouter();
	const searchParams = useSearchParams();

	const handleItemSelect = useCallback(
		(index: number) => {
			if (!autoCompleteResults) return;

			const {
				situsfullstreetaddress,
				situscity,
				situsstate,
				situszip5,
				fips,
				apn,
				county,
			} = autoCompleteResults[index].params;

			const addressData = {
				address: cleanAddress(situsfullstreetaddress),
				line2: "",
				city: toTitleCase(situscity),
				state: situsstate,
				zipcode: situszip5,
				fips,
				apn,
				county,
			};

			setAddress(addressData);

			if (searchParams.get("exemptions")) {
				setIsExemptionsEndToEndFlow(true);
				router.push("/appeal/savings");
			} else {
				trackEvent({
					eventName: constants.EVENTS.FORM_FLOW_ADDRESS,
					data: {
						referrer:
							"User submitted address through appeal address page autocomplete form",
					},
				});
				trackEvent({
					eventName:
						constants.EVENTS.Appeal_Specific_Address_Entry_ResultsSucess,
					data: {
						Address: query,
					},
				});
				router.push("/appeal/savings");
			}
		},
		[autoCompleteResults, setAddress, trackEvent, searchParams]
	);

	const handleSendToAppeal = () => {
		let cleanQuery = cleanAddressForAppeal(query);
		if (searchParams.get("exemptions")) {
			cleanQuery += cleanQuery.includes("?")
				? "&exemptions=true"
				: "?exemptions=true";

			trackEvent({
				eventName:
					constants.EVENTS.E2E_Exemptions_LP_Address_Entry_DontSeeAddress,
				data: {
					Address: query,
				},
			});
		} else {
			trackEvent({
				eventName:
					constants.EVENTS.Appeal_Specific_Address_Entry_DontSeeProperty,
				data: {
					Address: query,
				},
			});
		}
		startAppealFlow(`/appeal${cleanQuery}`);
	};

	const handleKeyPress = useCallback(
		e => {
			if (!autoCompleteResults.length || !query) return;

			if (e.key === "ArrowUp") {
				e.preventDefault();
				let newIndex;
				if (typeof activeIndex !== "undefined") {
					newIndex =
						activeIndex === 0
							? autoCompleteResults.length - 1
							: activeIndex - 1;
				} else {
					newIndex = autoCompleteResults.length - 1;
				}
				setActiveIndex(newIndex);
			}

			if (e.key === "ArrowDown") {
				e.preventDefault();
				let newIndex;
				if (typeof activeIndex !== "undefined") {
					newIndex =
						activeIndex === autoCompleteResults.length - 1
							? 0
							: activeIndex + 1;
				} else {
					newIndex = 0;
				}
				setActiveIndex(newIndex);
			}
		},
		[activeIndex, autoCompleteResults, query]
	);

	const handleInputChange: ChangeEventHandler<HTMLInputElement> = e => {
		setQuery(e.target.value);
	};

	const cleanAddressForAppeal = (userInput: string) => {
		const address = {
			address: "",
			city: "",
			state: "",
			zipcode: "",
		};
		userInput = userInput.trim().replace(/\s\s+/g, " ").replace(/ *, */g, " "); //replaces extra whitespaces + replaces commas with whitespace

		//Edge Case: We can assume that someone only typed in their first line of their address (ex: 123 Lily). This is for dealing with '123 California'
		if (userInput.split(" ").length <= 2) {
			return "?address=" + encodeURIComponent(userInput);
		}

		let tokens = userInput.split(" ").reverse();
		let tokensCopy = [...tokens];

		for (let i = 0; i < tokens.length; i++) {
			let currentToken = tokens[i];

			if (i === 0 || i === 1) {
				//Zipcode
				if (i === 0 && +currentToken * 0 === 0 && currentToken.length === 5) {
					address.zipcode = tokens[i];
					tokensCopy.splice(tokensCopy.indexOf(currentToken), 1);
				}

				//State
				//if value.length === 2, that means it can be a state in "TX" format. Else if value.length > 2, it can be in "Texas" format
				let stateFound;
				if (i === 0 || (address.zipcode !== "" && i === 1)) {
					if (
						currentToken.length === 2 &&
						currentToken.toUpperCase() !== "CT"
					) {
						stateFound =
							states[currentToken.toUpperCase() as keyof typeof states];
					} else if (currentToken.length > 2) {
						stateFound =
							Object.keys(states).find(
								key =>
									states[key as keyof typeof states].toUpperCase() ===
									currentToken.toUpperCase()
							) || "";
					}
					if (stateFound) {
						address.state = stateFound;
						tokensCopy.splice(tokensCopy.indexOf(currentToken), 1);
					}
				}
			}
		}
		address.address = tokensCopy.reverse().join(" "); //Address line 1

		return (
			"?address=" +
			encodeURIComponent(address.address) +
			"&zipcode=" +
			encodeURIComponent(address.zipcode) +
			"&state=" +
			encodeURIComponent(address.state)
		);
	};

	useEffect(() => {
		if (!showResults) setActiveIndex(undefined);
	}, [showResults]);

	useEffect(() => {
		if (showResults && query) {
			setActiveIndex(undefined);
		}
	}, [showResults, query]);

	useEffect(() => {
		trackEvent({
			eventName: constants.EVENTS.Form_Flow_address_entry_viewed,
		});
		const handler = () => {
			setShowResults(false);
		};

		window.addEventListener("click", handler);

		return () => {
			window.removeEventListener("click", handler);
		};
	}, []);

	useEffect(() => {
		if (query.length > 1 && autoCompleteResults?.length) {
			setShowResults(true);
		} else if (!query && !autoCompleteResults?.length) {
			setShowResults(false);
		}
	}, [autoCompleteResults, query]);

	return (
		<form
			id="appeal-address-input-container"
			className="appeal-address-autocomplete-input-container"
			onSubmit={e => {
				e.preventDefault();
				if (!autoCompleteResults.length) return;
				if (typeof activeIndex !== "undefined") {
					handleItemSelect(activeIndex);
				} else {
					handleItemSelect(0);
				}
			}}>
			<div id="appeal-address-autocomplete-container">
				<div id="landing-title-label-autocomplete-loading-spinner">
					<Label id="landing-title-input-label">Address*</Label>
					{autoCompleteLoading && (
						<div className="landing-title-autocomplete-results-loader">
							<NextImage
								src={SimpleSpinner}
								alt="Loading..."
								style={{
									maxWidth: "100%",
									height: "auto",
								}}
							/>
						</div>
					)}
				</div>
				<Search
					autoComplete="off"
					required={false}
					onChange={handleInputChange}
					handleRightIconClick={() => {
						if (autoCompleteResults?.length) {
							if (activeIndex === undefined) {
								handleSendToAppeal();
							} else {
								handleItemSelect(activeIndex);
							}
						}
					}}
					onKeyDown={handleKeyPress}
					id={"landing-title-input" + (showResults ? "-autocomplete-open" : "")}
					placeholder="Enter your property address"
				/>
				{showResults && (
					<div id="landing-title-autocomplete-results-wrapper">
						<div
							id="landing-title-autocomplete-results-container"
							onScrollCapture={e => {
								e.preventDefault();
								e.stopPropagation();
							}}
							onClick={e => {
								e.preventDefault();
								e.stopPropagation();
							}}>
							{autoCompleteResults?.slice(0, 3).map(({ label, params }, i) => {
								const showParcel =
									autoCompleteResults[i - 1]?.label === label ||
									autoCompleteResults[i + 1]?.label === label;

								return (
									<div
										key={label + i}
										className={
											"landing-title-autocomplete-item" +
											(i === activeIndex ? "-active" : "")
										}
										onMouseOver={() => setActiveIndex(i)}
										onClick={() => handleItemSelect(i)}>
										<p className="no-translate">{label}</p>
										{showParcel && (
											<label>
												Parcel:{" "}
												<span className="no-translate">{params.apn}</span>
											</label>
										)}
									</div>
								);
							})}
						</div>
						<div
							className="dont-see-address button-small"
							onClick={() => {
								handleSendToAppeal();
							}}>
							Don't see your address? Click here.
						</div>
					</div>
				)}
			</div>
		</form>
	);
};

const ManualAddressEntry: FC<{
	setAddressInputType: Dispatch<SetStateAction<"manual" | "autocomplete">>;
}> = ({ setAddressInputType }) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const [address, setAddress] = useState<IAddress>({
		address: "",
		line2: "",
		city: "",
		state: "",
		zipcode: "",
		apn: "",
	});

	const trackEvent = useEventTracker();
	const { userData } = useAuthContext();

	const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
		null
	);

	const [showResults, setShowResults] = useState(false);

	const { autoCompleteLoading, autoCompleteResults } = useAutocomplete(
		address.address
	);

	const {
		address: contextAddress,
		setAddress: setContextAddress,
		setSavingsData: setContextSavingsdata,
		setSession: setContextSession,
		setNonAutocompleteParcelNumber,
		setOwnedByCompany,
		setContactInfo,
		setAddressRaw,
		setIsTakeover,
		resetAppealFlow,
		isEndToEndExemptionsFlow,
	} = useAppealContext();

	useEffect(() => {
		if (isEndToEndExemptionsFlow) {
			trackEvent({
				eventName: constants.EVENTS.E2E_Exemptions_Address_Page_Viewed,
				data: {
					"User Type": userData?.id
						? "New (signed out)"
						: "Returning (signed in)",
				},
			});
		}
	}, [isEndToEndExemptionsFlow]);

	useEffect(() => {
		if (contextAddress && contextAddress.address) {
			setAddress(contextAddress);
		}
	}, [contextAddress]);

	const createFieldHandler = (
		key: keyof IAddress
	): ChangeEventHandler<HTMLInputElement | HTMLSelectElement> => {
		return e => {
			let val = e.target.value;
			// Custom validation for fields goes here
			if (key === "address" && e.target.value) {
				setShowResults(true);
			}

			setAddress(prev => ({
				...prev,
				[key]: val,
			}));
		};
	};

	const onAddressStepComplete = useCallback(
		async (address: IAddress) => {
			try {
				const { line1, line2 } = extractLine2({
					line1: address.address,
					line2: address.line2,
				});

				const clean: IAddress = {
					...address,
					address: cleanAddress(line1),
					line2: cleanAddress(line2),
					city: address.city,
				};

				if (!selectedItem) {
					const addressRaw =
						address.address +
						", " +
						(address.line2 ? " " + address.line2 + ", " : "") +
						address.city +
						", " +
						address.state +
						" " +
						address.zipcode;

					setAddressRaw(addressRaw);

					if (isEndToEndExemptionsFlow) {
						trackEvent({
							eventName:
								constants.EVENTS
									.E2E_Exemptions_LP_Address_Entry_Non_Auto_Complete,
							data: {
								Address: addressRaw,
							},
						});
					}
				} else if (isEndToEndExemptionsFlow) {
					const Address =
						address.address +
						", " +
						(address.line2 ? " " + address.line2 + ", " : "") +
						address.city +
						", " +
						address.state +
						" " +
						address.zipcode;

					if (isEndToEndExemptionsFlow) {
						trackEvent({
							eventName:
								constants.EVENTS.E2E_Exemptions_LP_Address_Entry_Auto_Complete,
							data: {
								Address,
							},
						});
					}
				}

				if (!isEndToEndExemptionsFlow) {
					trackEvent({
						eventName: constants.EVENTS.FORM_FLOW_ADDRESS,
					});
				}

				setContextAddress(clean);

				// Reset stale state from backwards nav
				setContextSession(undefined);
				setContextSavingsdata(undefined);
				setOwnedByCompany(undefined);
				setIsTakeover(undefined);
				setContactInfo(undefined);
				setNonAutocompleteParcelNumber(false);
				router.push("/appeal/savings");
			} catch (err) {
				console.error("Error geocoding: \n", err);
			}
		},
		[
			selectedItem,
			setAddressRaw,
			setContactInfo,
			setContextAddress,
			setContextSavingsdata,
			setContextSession,
			setIsTakeover,
			setNonAutocompleteParcelNumber,
			setOwnedByCompany,
			trackEvent,
			isEndToEndExemptionsFlow,
			searchParams,
		]
	);

	const handleSubmit: FormEventHandler<HTMLFormElement> = e => {
		e.preventDefault();
		onAddressStepComplete(address);
	};

	const onAutocompleteItemClick = useCallback(
		(item: AutocompleteItem) => {
			const { params } = item;

			setSelectedItem(item);
			setAddress({
				address: cleanAddress(params.situsfullstreetaddress) ?? "",
				line2: "",
				city: toTitleCase(params.situscity) ?? "",
				state: params.situsstate ?? "",
				zipcode: params.situszip5 ?? "",
				apn: params.apn ?? "",
				county: params.county ?? "",
				fips: params.fips ?? "",
			});

			if (isEndToEndExemptionsFlow) {
				trackEvent({
					eventName:
						constants.EVENTS.E2E_Exemptions_Address_Page_Entry_ResultsSuccess,
					data: {
						Address:
							cleanAddress(params.situsfullstreetaddress) ??
							"" + ", " + toTitleCase(params.situscity) ??
							"" + ", " + params.situsstate ??
							"" + " " + params.situszip5 ??
							"",
					},
				});
			}
			setShowResults(false);
		},
		[isEndToEndExemptionsFlow]
	);

	useEffect(() => {
		if (contextAddress && contextAddress.address) {
			setAddress(contextAddress);
		}
	}, [contextAddress]);

	// Parse the URL query params and set address state
	useEffect(() => {
		const address = searchParams.get("address");
		const city = searchParams.get("city");
		const state = searchParams.get("state");
		const zipcode = searchParams.get("zipcode");

		let allAddressValues = {
			address,
			city,
			state,
			zipcode,
		} as Partial<IAddress>;

		if (address?.length) {
			setShowResults(true);
		}

		for (let [_key, value] of Object.entries(allAddressValues)) {
			const key = _key as keyof IAddress;

			if (Array.isArray(value)) {
				continue;
			} else if (value === undefined) {
				allAddressValues[key] = "";
			} else {
				value = cleanUrlVals(key, value);
				allAddressValues[key] = value ?? "";
			}
		}

		if (Object.keys(allAddressValues).length) {
			setAddress({
				...(allAddressValues as IAddress),
				line2: "",
				county: "",
				apn: "",
			});
		}
	}, [searchParams]);

	useEffect(() => {
		if (
			selectedItem &&
			(address.address !==
				cleanAddress(selectedItem.params.situsfullstreetaddress) ||
				address.city !== toTitleCase(selectedItem.params.situscity) ||
				address.state !== selectedItem.params.situsstate ||
				address.zipcode !== selectedItem.params.situszip5)
		) {
			setSelectedItem(null);
			setAddress(p => ({
				...p,
				apn: undefined,
				fips: undefined,
				county: undefined,
			}));
		}
	}, [
		selectedItem,
		address.address,
		address.city,
		address.state,
		address.zipcode,
	]);

	return (
		<form id="appeal-address-manual-input-container" onSubmit={handleSubmit}>
			<div id="appeal-address-input-container">
				<TextField
					required
					label="Address"
					placeholder="Street Address"
					autoComplete="street-address"
					value={address["address"]}
					onChange={createFieldHandler("address")}
					containerClassName="appeal-address-address1"
				/>
				{!!autoCompleteResults.length && showResults && (
					<div className="appeal-address-autocomplete-results-outer-contianer">
						<div className="appeal-address-autocomplete-results-label-container">
							<Label>Suggested Addresses</Label>
							{autoCompleteLoading && (
								<div className="appeal-address-autocomplete-results-loader">
									<NextImage
										src={SimpleSpinner}
										alt="Loading..."
										style={{
											maxWidth: "100%",
											height: "auto",
										}}
									/>
								</div>
							)}
						</div>
						<div className="appeal-address-autocomplete-results-inner-contianer">
							{autoCompleteResults.slice(0, 3).map((item, i) => {
								const { label, params } = item;
								const showParcel =
									autoCompleteResults[i - 1]?.label === label ||
									autoCompleteResults[i + 1]?.label === label;
								return (
									<div
										key={label + params.apn}
										className="appeal-address-autocomplete-results-item"
										onClick={() => onAutocompleteItemClick(item)}>
										<p className="sm no-translate">{label}</p>
										{showParcel && (
											<Label>
												Parcel:{" "}
												<span className="no-translate">{params.apn}</span>
											</Label>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}
				<TextField
					placeholder="Apt / Suite / Unit"
					autoComplete="address-line2"
					value={address["line2"]}
					onChange={createFieldHandler("line2")}
					containerClassName="appeal-address-address2"
				/>
				<div className="appeal-address-city-state-zip">
					<TextField
						required
						label="City"
						placeholder="City"
						autoComplete="address-level2"
						value={address["city"]}
						onChange={createFieldHandler("city")}
						containerClassName="appeal-address-city"
					/>
					<StateSelect
						required
						label="State"
						placeholder="State"
						autoComplete="address-level1"
						value={address["state"]}
						onChange={createFieldHandler("state")}
						containerClassName="appeal-address-state"
					/>
					<TextField
						required
						numeric
						label="Zip"
						placeholder="00000"
						value={address["zipcode"]}
						autoComplete="postal-code"
						minLength={5}
						maxLength={5}
						pattern="\d{5,5}(-\d{4,4})?"
						onChange={createFieldHandler("zipcode")}
						containerClassName="appeal-address-zip"
					/>
				</div>
			</div>
			<div className={"appeal-address-cta-container "}>
				<div className="mx-auto">
					<ButtonV2
						size="large"
						className="appeal-get-savings-btn"
						type="submit"
						style={{ marginBottom: "24px" }}>
						{isEndToEndExemptionsFlow ? "Continue" : "Calculate Savings"}
					</ButtonV2>
				</div>
			</div>
		</form>
	);
};

const AddressStep: FC = () => {
	const [addressInputType, setAddressInputType] = useState<
		"manual" | "autocomplete"
	>("autocomplete");

	const { resetAppealFlow } = useAppealContext();

	const renderAutocompleteAddressEntry = (): JSX.Element => {
		switch (addressInputType) {
			case "autocomplete":
				return (
					<AutocompleteAddressEntry setAddressInputType={setAddressInputType} />
				);
			case "manual":
				return <ManualAddressEntry setAddressInputType={setAddressInputType} />;
		}
	};

	const router = useRouter();
	const searchParams = useSearchParams();

	useEffect(() => {
		if (searchParams.get("address")) {
			setAddressInputType("manual");
		}
	}, [searchParams]);

	useEffect(() => {
		resetAppealFlow();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="appeal-step">
			<Grid>
				<h4 id="appeal-address-title">
					Enter a property address to get&nbsp;started.
				</h4>
				{renderAutocompleteAddressEntry()}
				<div id="appeal-address-other-signup-methods">
					<div
						className="appeal-address-signup-method"
						onClick={() => {
							router.push("/appeal/parcel");
						}}>
						<p className="sm">Know your parcel number?</p>
						<QuickLink size="small">Enter It Here</QuickLink>
					</div>
					<div
						className="appeal-address-signup-method"
						onClick={() => router.push("/appeal/portfolio")}>
						<p className="sm">Have more than one property?</p>
						<QuickLink size="small">Enter It Here</QuickLink>
					</div>
				</div>
			</Grid>
		</div>
	);
};

export default withAppealFlow(AddressStep);
